import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';

interface AddPlatformModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (platformData: { name: string; description: string }) => void;
}

const AddPlatformModal: React.FC<AddPlatformModalProps> = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [errors, setErrors] = useState({
    name: '',
    description: '',
  });

  const handleClose = () => {
    // Reset form data when closing
    setFormData({ name: '', description: '' });
    setErrors({ name: '', description: '' });
    onClose();
  };

  const validateForm = () => {
    const newErrors = {
      name: '',
      description: '',
    };

    if (!formData.name.trim()) {
      newErrors.name = 'Platform name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Platform description is required';
    }

    setErrors(newErrors);
    return !newErrors.name && !newErrors.description;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave({
        name: formData.name.trim(),
        description: formData.description.trim(),
      });
      handleClose();
    }
  };

  const handleInputChange =
    (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: '',
        }));
      }
    };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '300px',
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Add Platform Category
        </Typography>
        <IconButton onClick={handleClose} size="small" sx={{ color: '#666' }} aria-label="close">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography
              id="platform-name-label"
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              Platform Name *
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter platform name"
              value={formData.name}
              onChange={handleInputChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              inputProps={{ 'aria-labelledby': 'platform-name-label' }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                },
              }}
            />
          </Box>

          <Box>
            <Typography
              id="platform-description-label"
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              Platform Description *
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              placeholder="Enter platform description"
              value={formData.description}
              onChange={handleInputChange('description')}
              error={!!errors.description}
              helperText={errors.description}
              inputProps={{ 'aria-labelledby': 'platform-description-label' }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            borderColor: '#e0e0e0',
            color: '#666',
            '&:hover': {
              borderColor: '#ccc',
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            textTransform: 'none',
            backgroundColor: '#1A55F9',
            '&:hover': {
              backgroundColor: '#0641e6',
            },
          }}
        >
          Add Platform
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPlatformModal;
